import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Bell, Globe, Moon, Palette, Clock, Shield, Monitor, Activity, CheckCircle, XCircle, KeyRound, UserCircle } from 'lucide-react';
import { profileService, userService, activityService } from '../services';
import type { AccountSettings as AccountSettingsType, Profile } from '../services';
import type { ActivityRecord } from '../services/activity.service';
import { authService } from '../services/auth.service';
import DataTable from '../components/common/DataTable';
import type { Column } from '../components/common/DataTable';
import ConfirmDialog from '../components/common/ConfirmDialog';
import ChangePasswordModal from '../components/common/ChangePasswordModal';
import { useToast } from '../components/common/ToastContainer';
import { useSettingsStore } from '../stores/settingsStore';
import { useAuthStore } from '../stores/authStore';

const TIMEZONE_OPTIONS = [
  { value: '', label: 'Use system default' },
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'America/New_York (Eastern)' },
  { value: 'America/Chicago', label: 'America/Chicago (Central)' },
  { value: 'America/Denver', label: 'America/Denver (Mountain)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (Pacific)' },
  { value: 'Europe/London', label: 'Europe/London (GMT)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST)' },
  { value: 'Australia/Perth', label: 'Australia/Perth (AWST)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZST)' },
];

const DATE_FORMAT_OPTIONS = [
  { value: '', label: 'Use system default' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
  { value: 'MMM D, YYYY', label: 'MMM D, YYYY' },
];

const TIME_FORMAT_OPTIONS = [
  { value: '', label: 'Use system default' },
  { value: '12h', label: '12-hour (AM/PM)' },
  { value: '24h', label: '24-hour' },
];

const AccountSettings = () => {
  const toast = useToast();
  const { fetchUserPrefs } = useSettingsStore();
  const { user } = useAuthStore();
  const userId = user?.id;
  const [_settings, setSettings] = useState<AccountSettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [formData, setFormData] = useState({
    language: 'English',
    timeZone: '',
    dateFormat: '',
    timeFormat: '',
    theme: 'Light',
    emailNotifications: true,
    deviceAlerts: true,
    securityAlerts: true,
    weeklyReports: false,
    marketingCommunications: false,
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const settingsData = await profileService.getSettings();
        setSettings(settingsData);
        setFormData({
          language: settingsData.language,
          timeZone: settingsData.timeZone ?? '',
          dateFormat: settingsData.dateFormat ?? '',
          timeFormat: settingsData.timeFormat ?? '',
          theme: settingsData.theme,
          emailNotifications: settingsData.emailNotifications,
          deviceAlerts: settingsData.deviceAlerts,
          securityAlerts: settingsData.securityAlerts,
          weeklyReports: settingsData.weeklyReports,
          marketingCommunications: settingsData.marketingCommunications,
        });
      } catch (err) {
        console.error('Failed to load settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const updatedSettings = await profileService.updateSettings({
        ...formData,
        timeZone: formData.timeZone || null,
        dateFormat: formData.dateFormat || null,
        timeFormat: formData.timeFormat || null,
      });
      setSettings(updatedSettings);
      await fetchUserPrefs();
      toast.success('Success', 'Settings saved successfully');
    } catch (err: any) {
      toast.error('Update Failed', `Failed to update settings: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Native security controls replace the Keycloak redirect.

  const handleDeleteAccount = async () => {
    try {
      await profileService.deleteAccount();
      toast.success('Success', 'Account deleted successfully');
      window.location.href = '/';
    } catch (err: any) {
      toast.error('Delete Failed', `Failed to delete account: ${err.message}`);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading settings...</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
          <p className="text-gray-600 mt-1">Manage your account preferences and security</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>

      {/* Profile & Security */}
      <ProfileSecurityCard />

      {/* Sessions & Activity */}
      {userId && <SessionsActivitySection userId={userId} />}

      {/* Preferences */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <Palette className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Preferences</h2>
        </div>

        <div className="space-y-4">
          <FormField
            icon={Globe}
            label="Language"
            description="Select your preferred language"
            value={formData.language}
            onChange={(e) => setFormData({ ...formData, language: e.target.value })}
            options={[
              { value: 'English', label: 'English' },
            ]}
            disabled
          />

          <FormField
            icon={Globe}
            label="Time Zone"
            description="Set your local time zone (empty = use system default)"
            value={formData.timeZone}
            onChange={(e) => setFormData({ ...formData, timeZone: e.target.value })}
            options={TIMEZONE_OPTIONS}
          />

          <FormField
            icon={Globe}
            label="Date Format"
            description="How dates should be displayed (empty = use system default)"
            value={formData.dateFormat}
            onChange={(e) => setFormData({ ...formData, dateFormat: e.target.value })}
            options={DATE_FORMAT_OPTIONS}
          />

          <FormField
            icon={Clock}
            label="Time Format"
            description="12-hour or 24-hour clock (empty = use system default)"
            value={formData.timeFormat}
            onChange={(e) => setFormData({ ...formData, timeFormat: e.target.value })}
            options={TIME_FORMAT_OPTIONS}
          />

          <FormField
            icon={Moon}
            label="Theme"
            description="Choose light or dark mode"
            value={formData.theme}
            onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
            options={[
              { value: 'Light', label: 'Light' },
            ]}
            disabled
          />
        </div>
      </div>

      {/* Notifications */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <Bell className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
        </div>

        <div className="space-y-4">
          <ToggleField
            label="Email Notifications"
            description="Receive email alerts for important events"
            checked={formData.emailNotifications}
            onChange={(checked) => setFormData({ ...formData, emailNotifications: checked })}
          />

          <ToggleField
            label="Device Alerts"
            description="Get notified when devices go offline or have issues"
            checked={formData.deviceAlerts}
            onChange={(checked) => setFormData({ ...formData, deviceAlerts: checked })}
          />

          <ToggleField
            label="Security Alerts"
            description="Receive alerts for suspicious activity"
            checked={formData.securityAlerts}
            onChange={(checked) => setFormData({ ...formData, securityAlerts: checked })}
          />

          <ToggleField
            label="Weekly Reports"
            description="Receive weekly summary reports via email"
            checked={formData.weeklyReports}
            onChange={(checked) => setFormData({ ...formData, weeklyReports: checked })}
          />

          <ToggleField
            label="Marketing Communications"
            description="Product updates and announcements"
            checked={formData.marketingCommunications}
            onChange={(checked) => setFormData({ ...formData, marketingCommunications: checked })}
          />
        </div>
      </div>

      {/* Danger Zone */}
      <div className="card border-danger-200">
        <h2 className="text-lg font-semibold text-danger-600 mb-4">Danger Zone</h2>

        <div className="flex items-center justify-between p-4 bg-danger-50 rounded-lg">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Delete Account</h3>
            <p className="text-xs text-gray-500 mt-0.5">Permanently delete your account and all associated data</p>
          </div>
          <button
            onClick={() => setIsDeleteConfirmOpen(true)}
            className="btn-danger text-sm"
          >
            Delete Account
          </button>
        </div>
      </div>

      {/* Delete Account Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDeleteAccount}
        title="Delete Account"
        message="Are you sure you want to permanently delete your account? This action cannot be undone and all your data will be lost."
        confirmText="Delete Account"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

/**
 * Profile & Security card.
 *
 * Replaces the old "Managed by Authentication Provider" banner. Lets the user:
 *  - update their display name (first/last), pushed to both our DB and KC
 *  - change their password (native flow — API verifies current password via
 *    KC password grant, pushes new password via Admin API)
 *
 * MFA enrollment is scoped for a later pass (KC CONFIGURE_TOTP required action).
 */
const ProfileSecurityCard = () => {
  const toast = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const p = await profileService.getProfile();
        setProfile(p);
        const parts = (p.fullName || '').trim().split(/\s+/);
        setFirstName(parts[0] || '');
        setLastName(parts.slice(1).join(' '));
      } catch {
        // Non-fatal — the profile card will just show empty fields
      }
    })();
  }, []);

  const handleSaveName = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Invalid name', 'Please enter both first and last name');
      return;
    }
    setSaving(true);
    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      const updated = await profileService.updateProfile({ fullName });
      setProfile(updated);
      toast.success('Saved', 'Your name has been updated');
    } catch (err: any) {
      toast.error('Update failed', err?.message || 'Could not update your name');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (data: { currentPassword: string; newPassword: string; confirmPassword: string }) => {
    try {
      await profileService.changePassword(data);
      toast.success('Password changed', 'Your password has been updated');
      setShowPasswordModal(false);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.message ||
        'Could not change your password';
      toast.error('Change failed', msg);
      throw err; // keep the modal open so the user can retry
    }
  };

  // Load password policy once and hand it to the change-password modal
  useEffect(() => {
    authService.getPasswordPolicy().catch(() => null);
  }, []);

  return (
    <>
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <UserCircle className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Profile & Security</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">First name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Last name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
            <input
              type="email"
              value={profile?.email || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-600"
            />
            <p className="mt-1 text-xs text-gray-500">
              Your email is the identity we use for sign-in; contact an administrator to change it.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 pt-4">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-primary-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Password</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Change your sign-in password at any time.
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setShowPasswordModal(true)}
              className="btn-secondary text-sm inline-flex items-center space-x-1.5"
            >
              <KeyRound className="w-4 h-4" />
              <span>Change password</span>
            </button>
            <button
              type="button"
              onClick={handleSaveName}
              disabled={saving}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save name'}
            </button>
          </div>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSave={handleChangePassword}
      />
    </>
  );
};

// Sessions & Activity inline-tabbed section
const SessionsActivitySection = ({ userId }: { userId: string }) => {
  const [activeTab, setActiveTab] = useState<'sessions' | 'activity'>('sessions');

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Monitor className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Sessions & Activity</h2>
        </div>
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('sessions')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'sessions' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="inline-flex items-center space-x-1.5">
              <Monitor className="w-3.5 h-3.5" />
              <span>Sessions</span>
            </span>
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'activity' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="inline-flex items-center space-x-1.5">
              <Activity className="w-3.5 h-3.5" />
              <span>Activity</span>
            </span>
          </button>
        </div>
      </div>

      {activeTab === 'sessions' ? (
        <SessionsPanel userId={userId} />
      ) : (
        <ActivityPanel userId={userId} />
      )}
    </div>
  );
};

// Sessions panel — reuses pattern from UserDetail.tsx SessionsTab
const SessionsPanel = ({ userId }: { userId: string }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const [sessions, setSessions] = useState<any[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [revokingAll, setRevokingAll] = useState(false);

  const fetchSessions = async (page = 1) => {
    try {
      setSessionsLoading(true);
      const result = await userService.getUserSessions(userId, { page, pageSize: 20 });
      setSessions(result.items || []);
      setSessionTotal(result.pagination?.total || 0);
    } catch {
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => { fetchSessions(); }, [userId]);

  const handleRevokeAll = async () => {
    try {
      setRevokingAll(true);
      await userService.revokeAllSessions(userId);
      // Revoking all sessions includes the current one — clear local state
      // and land back on the native login page.
      logout();
      navigate('/login', { replace: true });
    } catch {
      toast.error('Failed', 'Failed to revoke sessions');
      setRevokingAll(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await userService.exportSessionsCSV(userId);
      const blob = response instanceof Blob ? response : new Blob([response], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sessions_${userId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export Failed', 'Failed to export sessions');
    }
  };

  const truncateUA = (ua: string | null) => {
    if (!ua) return 'Unknown';
    return ua.length > 60 ? ua.substring(0, 60) + '...' : ua;
  };

  const sessionColumns: Column<any>[] = [
    { key: 'ipAddress', label: 'IP Address', sortable: true, render: (s) => <span className="font-mono text-sm">{s.ipAddress || '-'}</span> },
    { key: 'userAgent', label: 'Device', sortable: false, render: (s) => <span className="text-sm text-gray-600" title={s.userAgent || ''}>{truncateUA(s.userAgent)}</span> },
    { key: 'createdAt', label: 'Created', sortable: true, render: (s) => <span className="text-sm">{s.createdAt ? new Date(s.createdAt).toLocaleString() : '-'}</span> },
    { key: 'lastActivity', label: 'Last Activity', sortable: true, render: (s) => <span className="text-sm">{s.lastActivity ? new Date(s.lastActivity).toLocaleString() : '-'}</span> },
    { key: 'expiresAt', label: 'Expires', sortable: true, render: (s) => <span className="text-sm">{s.expiresAt ? new Date(s.expiresAt).toLocaleString() : '-'}</span> },
    {
      key: 'status', label: 'Status', sortable: true,
      render: (s) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          s.status === 'Active' ? 'bg-success-100 text-success-700' :
          s.status === 'Revoked' ? 'bg-red-100 text-red-700' :
          'bg-gray-100 text-gray-700'
        }`}>{s.status}</span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{sessionTotal} total sessions</p>
        <div className="flex space-x-2">
          <button onClick={handleExportCSV} className="btn-secondary text-sm inline-flex items-center space-x-1">
            <span>Export CSV</span>
          </button>
          <button
            onClick={handleRevokeAll}
            disabled={revokingAll}
            className="btn-secondary text-sm text-red-600 border-red-200 hover:bg-red-50 inline-flex items-center space-x-1"
          >
            <span>{revokingAll ? 'Revoking...' : 'Revoke All Active'}</span>
          </button>
        </div>
      </div>
      <DataTable
        columns={sessionColumns}
        data={sessions}
        searchPlaceholder="Search by IP or user agent..."
        loading={sessionsLoading}
        pageSize={5}
      />
    </div>
  );
};

// Activity panel — reuses pattern from UserDetail.tsx ActivityTab
const ActivityPanel = ({ userId }: { userId: string }) => {
  const toast = useToast();
  const [activities, setActivities] = useState<ActivityRecord[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activityTotal, setActivityTotal] = useState(0);

  const fetchActivities = async (page = 1) => {
    try {
      setActivitiesLoading(true);
      const result = await activityService.getByUser(userId, { page, pageSize: 20 });
      setActivities(result.items || []);
      setActivityTotal(result.pagination?.total || 0);
    } catch {
      setActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  };

  useEffect(() => { fetchActivities(); }, [userId]);

  const handleExportCSV = async () => {
    try {
      const response = await activityService.exportCSV(userId);
      const blob = response instanceof Blob ? response : new Blob([response], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity_${userId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export Failed', 'Failed to export activity log');
    }
  };

  const actionColors: Record<string, string> = {
    create: 'bg-green-100 text-green-700',
    update: 'bg-blue-100 text-blue-700',
    delete: 'bg-red-100 text-red-700',
    login: 'bg-purple-100 text-purple-700',
    logout: 'bg-gray-100 text-gray-700',
    revoke: 'bg-orange-100 text-orange-700',
    assign: 'bg-teal-100 text-teal-700',
    unassign: 'bg-amber-100 text-amber-700',
    sync: 'bg-indigo-100 text-indigo-700',
  };

  const activityColumns: Column<ActivityRecord>[] = [
    {
      key: 'created_at', label: 'Timestamp', sortable: true,
      render: (a) => <span className="text-sm">{a.created_at ? new Date(a.created_at).toLocaleString() : '-'}</span>,
    },
    {
      key: 'action', label: 'Action', sortable: true,
      render: (a) => <span className={`px-2 py-1 text-xs font-medium rounded-full ${actionColors[a.action] || 'bg-gray-100 text-gray-700'}`}>{a.action}</span>,
    },
    { key: 'resource_type', label: 'Resource Type', sortable: true, render: (a) => <span className="text-sm">{a.resource_type}</span> },
    {
      key: 'resource_id', label: 'Resource',
      render: (a) => <span className="text-sm text-gray-600">{a.resource_name || a.resource_id || '-'}</span>,
    },
    { key: 'ip_address', label: 'IP', render: (a) => <span className="text-sm font-mono">{a.ip_address || '-'}</span> },
    {
      key: 'status', label: 'Status', sortable: true,
      render: (a) => a.status === 'success' ? (
        <span className="inline-flex items-center space-x-1 text-success-700"><CheckCircle className="w-4 h-4" /><span>Success</span></span>
      ) : (
        <span className="inline-flex items-center space-x-1 text-red-600"><XCircle className="w-4 h-4" /><span>Failed</span></span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{activityTotal} total events</p>
        <button onClick={handleExportCSV} className="btn-secondary text-sm inline-flex items-center space-x-1">
          <span>Export CSV</span>
        </button>
      </div>
      <DataTable
        columns={activityColumns}
        data={activities}
        searchPlaceholder="Search activity..."
        loading={activitiesLoading}
        pageSize={5}
      />
    </div>
  );
};

const FormField = ({
  icon: Icon,
  label,
  description,
  value,
  onChange,
  options,
  disabled = false,
}: {
  icon: typeof Globe;
  label: string;
  description: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) => (
  <div className="flex items-center justify-between">
    <div className="flex items-start space-x-3">
      <Icon className="w-4 h-4 text-gray-400 mt-0.5" />
      <div>
        <label className="text-sm font-medium text-gray-900">{label}</label>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`input-base w-56 text-sm ${disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

const ToggleField = ({
  label,
  description,
  checked,
  onChange
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <div className="flex items-center justify-between">
    <div>
      <label className="text-sm font-medium text-gray-900">{label}</label>
      <p className="text-xs text-gray-500 mt-0.5">{description}</p>
    </div>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
    </label>
  </div>
);

export default AccountSettings;
